import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FiArrowLeft } from 'react-icons/fi';

import { apiJson } from '@/lib/api';
import type { CalendarEvent } from '@/lib/types';

function EventDetailPage() {
  const { eventId } = Route.useParams();
  const search = Route.useSearch();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const eventQuery = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => apiJson<CalendarEvent>(getToken, `/v1/events/${eventId}`),
  });

  const deleteAllMutation = useMutation({
    mutationFn: () => apiJson<void>(getToken, `/v1/events/${eventId}?scope=all`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      navigate({ to: '/app/calendar' });
    },
  });

  const deleteOccurrenceMutation = useMutation({
    mutationFn: (originalStartAt: string) =>
      apiJson<void>(
        getToken,
        `/v1/events/${eventId}/occurrences/${encodeURIComponent(originalStartAt)}`,
        { method: 'DELETE' }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });

  const deleteFutureMutation = useMutation({
    mutationFn: (from: string) =>
      apiJson<void>(
        getToken,
        `/v1/events/${eventId}?scope=this_and_future&from=${encodeURIComponent(from)}`,
        { method: 'DELETE' }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      navigate({ to: '/app/calendar' });
    },
  });

  const event = eventQuery.data;
  const isRecurring = Boolean(event?.rrule);
  const canScopeDelete = Boolean(isRecurring && search.original_start_at);

  return (
    <div className='min-h-screen bg-sky-light'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-4'>
          <Link to='/app/calendar' className='inline-flex items-center gap-2 text-primary hover:underline'>
            <FiArrowLeft /> Back to Calendar
          </Link>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6'>
        {!event ? (
          <div className='bg-white rounded-2xl border border-gray-200 p-5'>Loading event…</div>
        ) : (
          <div className='bg-white rounded-2xl border border-gray-200 p-5 space-y-3'>
            <h1 className='text-2xl font-bold text-gray-900'>{event.title}</h1>
            <p className='text-gray-600'>
              {event.is_all_day
                ? 'All day'
                : new Date(event.start_at).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
            </p>
            {event.location ? <p className='text-gray-600'>Location: {event.location}</p> : null}
            {event.notes ? <p className='text-gray-600'>Notes: {event.notes}</p> : null}
            {event.rrule ? <p className='text-sm text-primary font-semibold'>Recurring event</p> : null}

            <div className='pt-4 flex flex-wrap gap-2'>
              <Link
                to='/app/calendar/events/add'
                search={{ event_id: String(event.id) }}
                className='rounded-xl bg-primary text-white px-4 py-2 font-semibold hover:bg-primary-hover transition-colors'
              >
                Edit Event
              </Link>
              <button
                type='button'
                onClick={() => deleteAllMutation.mutate()}
                className='rounded-xl bg-red-600 text-white px-4 py-2 font-semibold hover:bg-red-700 transition-colors'
              >
                Delete All
              </button>
              {canScopeDelete && search.original_start_at ? (
                <>
                  <button
                    type='button'
                    onClick={() => deleteOccurrenceMutation.mutate(search.original_start_at!)}
                    className='rounded-xl bg-red-100 text-red-700 px-4 py-2 font-semibold hover:bg-red-200 transition-colors'
                  >
                    Delete This Occurrence
                  </button>
                  <button
                    type='button'
                    onClick={() => deleteFutureMutation.mutate(search.original_start_at!)}
                    className='rounded-xl bg-red-100 text-red-700 px-4 py-2 font-semibold hover:bg-red-200 transition-colors'
                  >
                    Delete This + Following
                  </button>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/events/$eventId')({
  component: EventDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    original_start_at:
      typeof search.original_start_at === 'string'
        ? search.original_start_at
        : undefined,
  }),
});
